import React, { useState } from 'react';
import { AlertCircleIcon, SparklesIcon } from 'lucide-react';
interface InputStepProps {
  onSubmit: (input: string) => void;
  initialValue?: string;
}
const MIN_CHARS = 10;
const MAX_CHARS = 2000;
const EXAMPLE_DILEMMA = "I'm deciding whether to leave my stable corporate job to start my own company. I have a good salary and benefits, but I feel unfulfilled and believe I have a unique product idea. I have about 18 months of savings. Should I take the leap or wait until I have more runway?";
export function InputStep({
  onSubmit,
  initialValue = ''
}: InputStepProps) {
  const [input, setInput] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const charCount = input.length;
  const isValidLength = charCount >= MIN_CHARS && charCount <= MAX_CHARS;
  const isTooShort = charCount > 0 && charCount < MIN_CHARS;
  const isTooLong = charCount > MAX_CHARS;
  const handleSubmit = () => {
    setTouched(true);
    if (charCount === 0) {
      setError('Please describe your decision or dilemma.');
      return;
    }
    if (isTooShort) {
      setError(`Please provide more detail. Minimum ${MIN_CHARS} characters.`);
      return;
    }
    if (isTooLong) {
      setError(`Input is too long. Maximum ${MAX_CHARS} characters.`);
      return;
    }
    setError(null);
    onSubmit(input.trim());
  };
  const handleTryExample = () => {
    setInput(EXAMPLE_DILEMMA);
    setError(null);
    setTouched(false);
  };
  const handleInputChange = (value: string) => {
    setInput(value);
    if (touched && error) {
      setError(null);
    }
  };
  const getCharCountColor = () => {
    if (isTooLong) return 'text-destructive';
    if (isTooShort && touched) return 'text-destructive';
    if (charCount >= MIN_CHARS) return 'text-muted-foreground';
    return 'text-muted-foreground';
  };
  return <div className="w-full max-w-2xl mx-auto" data-id="element-16">
      <div className="text-center mb-8" data-id="element-17">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-4" data-id="element-18">
          <SparklesIcon className="w-6 h-6 text-foreground" data-id="element-19" />
        </div>
        <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-3 tracking-tight" data-id="element-20">
          Reframe any decision through Naval's lens.
        </h1>
        <p className="text-muted-foreground text-lg" data-id="element-21">
          Gain clarity using principles of leverage, specific knowledge, and
          long-term thinking.
        </p>
      </div>

      <div className="space-y-4" data-id="element-22">
        <div className="relative" data-id="element-23">
          <textarea value={input} onChange={e => handleInputChange(e.target.value)} onBlur={() => setTouched(true)} placeholder="Describe your decision or dilemma here..." className={`
              w-full min-h-[200px] p-4 rounded-lg border bg-card text-foreground
              placeholder:text-muted-foreground resize-y
              focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent
              transition-colors
              ${error ? 'border-destructive' : 'border-border'}
            `} aria-label="Decision or dilemma input" aria-describedby="char-count error-message" data-id="element-24" />

          <div className="flex items-center justify-between mt-2" data-id="element-25">
            <button type="button" onClick={handleTryExample} className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors" data-id="element-26">
              Try an example
            </button>

            <span id="char-count" className={`text-sm ${getCharCountColor()}`} aria-live="polite" data-id="element-27">
              {charCount} / {MAX_CHARS}
            </span>
          </div>
        </div>

        {error && <div id="error-message" className="flex items-start gap-2 text-destructive text-sm" role="alert" data-id="element-28">
            <AlertCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-29" />
            <span data-id="element-30">{error}</span>
          </div>}

        <button type="button" onClick={handleSubmit} disabled={charCount === 0} className={`
            w-full py-3 px-6 rounded-lg font-medium text-base
            transition-all duration-200
            ${charCount === 0 ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.98]'}
          `} aria-label="Reframe decision" data-id="element-31">
          Reframe
        </button>
      </div>
    </div>;
}